import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import { toast } from 'react-toastify';
import api, { SESSION_EXPIRED_EVENT } from '../services/api';

// Create the context
export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize auth state from localStorage
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    
    if (storedUser && token) {
      // Check if token is expired
      try {
        const tokenParts = token.split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          const expiryDate = new Date(payload.exp * 1000);
          const isExpired = expiryDate < new Date();
          
          if (isExpired) {
            // Token is expired, clear storage and don't set user
            console.warn('Token is expired on app initialization, clearing session');
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            toast.info('Your previous session has expired. Please log in again.');
          } else {
            setUser(JSON.parse(storedUser));
          }
        } else {
          setUser(JSON.parse(storedUser));
        }
      } catch (e) {
        console.error('Error parsing JWT token:', e);
        setUser(JSON.parse(storedUser));
      }
    }
    
    setLoading(false);
  }, []);
  
  // Listen for session expiration events
  useEffect(() => {
    const handleSessionExpired = (event) => {
      // Clear user state
      setUser(null);
      
      // Show toast notification
      toast.error(event.detail.message || 'Your session has expired. Please log in again.');
    };
    
    // Add event listener
    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    
    // Clean up
    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    };
  }, []);

  // Helper function to check if a user has admin privileges
  const isAdminUser = (userObj) => {
    if (!userObj) return false;
    
    // Check various possible admin role formats
    return (
      userObj.role === 'admin' || 
      userObj.role === 'ADMIN' || 
      userObj.userType === 'admin' ||
      userObj.isAdmin === true ||
      (userObj.roles && (
        userObj.roles.includes('admin') || 
        userObj.roles.includes('ADMIN')
      )) ||
      userObj.user_type === 'admin'
    );
  };

  // Login function
  const login = useCallback(async (credentials) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.post('/api/v1/auth/login', credentials);
      
      if (response.data.success) {
        let { user, token } = response.data.data;
        
        // DEBUG: Log the user object to see its structure
        console.log('User object from API:', JSON.stringify(user, null, 2));
        
        // If this is an admin login attempt, ensure the role is set correctly
        if (credentials.isAdmin) {
          // DEBUG: Log the admin check results
          console.log('Admin check results:', {
            'role === admin': user.role === 'admin',
            'role === ADMIN': user.role === 'ADMIN',
            'userType === admin': user.userType === 'admin',
            'isAdmin === true': user.isAdmin === true,
            'has roles array': !!user.roles,
            'user_type === admin': user.user_type === 'admin',
            'user object keys': Object.keys(user)
          });
          
          // TEMPORARY: Force admin access for admin@example.com for testing
          if (credentials.email === 'admin@example.com') {
            console.log('Granting admin access to admin@example.com for testing');
            // Set admin role explicitly
            user = {
              ...user,
              role: 'admin',
              isAdminSession: true
            };
          }
          // Regular admin check (commented out temporarily)
          else if (!isAdminUser(user)) {
            throw new Error('You do not have admin privileges');
          }
          else {
            // Mark this as an admin login session
            user = {
              ...user,
              isAdminSession: true
            };
          }
        }
        
        // Store in localStorage
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        
        // Update state
        setUser(user);
        
        // Check if there's a redirect path stored from a previous session expiration
        const redirectPath = localStorage.getItem('redirectAfterLogin');
        if (redirectPath) {
          // Clear the stored redirect path
          localStorage.removeItem('redirectAfterLogin');
          
          // Show success message with redirect info
          toast.success(`Login successful! Redirecting you back to your previous page.`);
          
          // Use a small timeout to ensure the toast is visible before redirect
          setTimeout(() => {
            window.location.href = redirectPath;
          }, 1500);
        } else {
          toast.success('Login successful!');
        }
        
        return { success: true };
      } else {
        throw new Error(response.data.message || 'Login failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'An error occurred during login');
      return { success: false, error: err.response?.data?.message || err.message || 'An error occurred during login' };
    } finally {
      setLoading(false);
    }
  }, []);

  // Register function
  const register = useCallback(async (userData) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.post('/api/v1/auth/register', userData);
      
      if (response.data.success) {
        const { user, token } = response.data.data;
        
        // Store in localStorage
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        
        // Update state
        setUser(user);
        return { success: true };
      } else {
        throw new Error(response.data.message || 'Registration failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'An error occurred during registration');
      return { 
        success: false, 
        error: err.response?.data?.message || err.message || 'An error occurred during registration',
        errors: err.response?.data?.errors || {}
      };
    } finally {
      setLoading(false);
    }
  }, []);

  // Logout function
  const logout = useCallback(async (redirectUrl) => {
    try {
      setLoading(true);
      
      // Check if the user is an admin or on an admin page
      const isAdmin = user && isAdminUser(user);
      const isAdminSession = user && user.isAdminSession;
      const isOnAdminPage = window.location.pathname.includes('/admin');
      
      // Call logout API
      await api.post('/api/v1/auth/logout');
      
      // Clear local storage and state
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
      
      // Show logout success message
      toast.success('You have been logged out successfully');
      
      // Determine where to redirect after logout
      if (redirectUrl) {
        // If a specific redirect URL was provided, use it
        window.location.href = redirectUrl;
      } else if (isAdmin || isAdminSession || isOnAdminPage) {
        // If user is an admin or on an admin page, redirect to admin login
        window.location.href = '/admin/login';
      } else {
        // Otherwise redirect to regular login
        window.location.href = '/login';
      }
    } catch (err) {
      console.error('Logout error:', err);
      
      // Even if the API call fails, clear local storage and state
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
      
      // Still redirect based on user type
      if (window.location.pathname.includes('/admin')) {
        window.location.href = '/admin/login';
      } else {
        window.location.href = '/login';
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Check if user is authenticated
  const isAuthenticated = useCallback(() => {
    return !!user;
  }, [user]);

  // Check if user has a specific role
  const hasRole = useCallback((role) => {
    if (!user) return false;
    
    if (role === 'admin') {
      return isAdminUser(user);
    }
    
    // For other roles, check different possible formats
    return (
      user.role === role || 
      (user.roles && user.roles.includes(role)) ||
      user.userType === role ||
      user.user_type === role
    );
  }, [user]);

  // Update user profile
  const updateProfile = useCallback(async (profileData) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.put('/auth/profile', profileData);
      
      if (response.data.status === 'success') {
        const updatedUser = response.data.data.user;
        
        // Update localStorage
        localStorage.setItem('user', JSON.stringify(updatedUser));
        
        // Update state
        setUser(updatedUser);
        return { success: true };
      } else {
        throw new Error(response.data.message || 'Profile update failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'An error occurred during profile update');
      return { 
        success: false, 
        error: err.response?.data?.message || err.message || 'An error occurred during profile update',
        errors: err.response?.data?.errors || {}
      };
    } finally {
      setLoading(false);
    }
  }, []);

  // Forgot password
  const forgotPassword = useCallback(async (email) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.post('/auth/forgot-password', { email });
      
      if (response.data.status === 'success') {
        return { success: true, message: response.data.message };
      } else {
        throw new Error(response.data.message || 'Password reset request failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'An error occurred');
      return { 
        success: false, 
        error: err.response?.data?.message || err.message || 'An error occurred'
      };
    } finally {
      setLoading(false);
    }
  }, []);

  // Reset password
  const resetPassword = useCallback(async (token, password, password_confirmation) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.post('/auth/reset-password', { 
        token, 
        password, 
        password_confirmation 
      });
      
      if (response.data.status === 'success') {
        return { success: true, message: response.data.message };
      } else {
        throw new Error(response.data.message || 'Password reset failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'An error occurred');
      return { 
        success: false, 
        error: err.response?.data?.message || err.message || 'An error occurred',
        errors: err.response?.data?.errors || {}
      };
    } finally {
      setLoading(false);
    }
  }, []);

  // Context value
  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    isAuthenticated,
    hasRole,
    updateProfile,
    forgotPassword,
    resetPassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
